import { Modal } from "@material-ui/core";
import React from "react";
import styled from "styled-components";
import theme from "../../../../assets/theme";
import { CloseBtn } from "../../../pages/AssignCaseLifeCycleBulkCase";
import { COLORS } from "../HearingBundles/tokens";
import { Form, Formik } from "formik";
import FormField from "../../../common/FormField/FormField";
import { PrimaryCTAButton } from "../../../common/Buttons";
import useLoader from "../../../../hooks/useLoader";
import { useSnackbar } from "notistack";
import { getErrorMessage } from "../../../../helpers/functions";
import CaseService from "../../../../services/CaseService";

const getSideColor = (side) => {
  return side === "claimant"
    ? { dot: "#F59E0B", bg: "#FEF3E2", fg: "#B45309", border: "#FCD34D" }
    : { dot: "#3B82F6", bg: "#EFF6FF", fg: "#1E40AF", border: "#93C5FD" };
};

const AgentDetailsModal = ({
  onClose,
  partyData,
  caseDetails,
  setRespondentModal,
  setRefreshDetails,
}) => {
  if (!partyData) return null;

  const partyRole = partyData.partyRole;
  let rawParty = partyData.party || {};
  if (rawParty?.party) rawParty = rawParty.party;

  let agents = [];

  if (rawParty?.agents?.length > 0) {
    agents = rawParty.agents;
  } else if (partyRole === "respondent") {
    const respondentParties =
      caseDetails?.respondentParties || rawParty?.respondentParties || [];
    if (respondentParties.length > 0) {
      agents = respondentParties.map((resp, index) => ({
        id: resp.id || `resp-${index}`,
        role: "",
        email: resp.email || "",
        mobile: resp.mobile || "",
        user: { name: resp.name || "", mobile: resp.mobile || "" },
        name: resp.name || "",
      }));
    }
  }

  const partyName =
    rawParty?.name ||
    rawParty?.respondentName ||
    caseDetails?.respondentParties?.[0]?.name ||
    "Party Name";

  const sideColor = getSideColor(partyRole);
  const isRespondent = partyRole === "respondent";
  const canEditEmail =
    isRespondent && caseDetails?.status === "awaitingRespondentAcceptance";

  return (
    <Modal open={true} onClose={onClose} disableRestoreFocus>
      <ModalOverlay>
        <ModalWrapper>
          <ModalHeader>
            <HeaderLeft>
              <ModalAccent color={sideColor.dot} />
              <HeaderContent>
                <ModalTitle>Agent Details</ModalTitle>
                <ModalSubtitle>
                  {partyRole === "claimant" ? "Claimant" : "Respondent"} •{" "}
                  {partyName}
                </ModalSubtitle>
              </HeaderContent>
            </HeaderLeft>

            <HeaderActions>
              {isRespondent &&
                caseDetails?.resolutionKind === "arbitration" &&
                ![
                  "draft",
                  "pendingRegistrationFee",
                  "proformaGenerated",
                  "waitingForCaseApproval",
                ].includes(caseDetails?.status) && (
                  <AddButton onClick={() => setRespondentModal(true)}>
                    + Add Agent
                  </AddButton>
                )}

              <CloseWrapper>
                <CloseBtn onClick={onClose}>
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                  >
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </CloseBtn>
              </CloseWrapper>
            </HeaderActions>
          </ModalHeader>

          <ModalBody>
            {agents.length === 0 ? (
              <EmptyState>
                <EmptyTitle>No Agents Added</EmptyTitle>
                <EmptySubtitle>
                  This party hasn't added any agents yet.
                </EmptySubtitle>
              </EmptyState>
            ) : (
              <AgentTableView
                agents={agents}
                canEditEmail={canEditEmail}
                caseId={caseDetails?.id}
                onClose={onClose}
                {...{ setRefreshDetails }}
              />
            )}
          </ModalBody>
        </ModalWrapper>
      </ModalOverlay>
    </Modal>
  );
};

export default AgentDetailsModal;

const EditableEmail = ({ agent, caseId, onClose, setRefreshDetails }) => {
  const { setLoader } = useLoader();
  const { enqueueSnackbar } = useSnackbar();

  const onFormSubmit = async (values, { resetForm }) => {
    try {
      setLoader({ state: true, message: "Updating Email..." });

      const payload = {
        oldEmail: values.oldEmail,
        newEmail: values.newEmail,
      };

      const response = await CaseService.updateCaseEmail(caseId, payload);

      if (response) {
        enqueueSnackbar("Email updated successfully", { variant: "success" });
        resetForm(); // Optional: reset after success
      }
    } catch (error) {
      const message = getErrorMessage(error);
      enqueueSnackbar(message, { variant: "error" });
    } finally {
      setLoader({ state: false });
      onClose();
      setRefreshDetails(true);
    }
  };

  return (
    <Formik
      initialValues={{
        oldEmail: agent?.email || "",
        newEmail: agent?.email || "",
      }}
      enableReinitialize
      onSubmit={onFormSubmit}
    >
      {({ values, handleChange, handleSubmit, isValid, touched, errors }) => (
        <EmailForm onSubmit={handleSubmit}>
          <FormField
            input={{
              name: "newEmail",
              label: "",
              type: "email",
              required: true,
            }}
            values={values}
            handleChange={handleChange}
            touched={touched}
            errors={errors}
          />

          <UpdateButtonWrap>
            <PrimaryCTAButton
              type="submit"
              disabled={!isValid || values.newEmail === values.oldEmail}
            >
              Update
            </PrimaryCTAButton>
          </UpdateButtonWrap>
        </EmailForm>
      )}
    </Formik>
  );
};

const AgentTableView = ({
  agents,
  canEditEmail,
  caseId,
  onClose,
  setRefreshDetails,
}) => (
  <TableContainer>
    <Table>
      <colgroup>
        <col style={{ width: "22%" }} />
        <col style={{ width: "40%" }} />
        <col style={{ width: "18%" }} />
        <col style={{ width: "20%" }} />
      </colgroup>
      <thead>
        <tr>
          <Th>Agent</Th>
          <Th>Email</Th>
          <Th>Mobile</Th>
          <Th>Role</Th>
        </tr>
      </thead>
      <tbody>
        {agents.map((agent) => (
          <TableRow key={agent.id}>
            <Td>
              <AgentName>{agent.user?.name || agent.name || "—"}</AgentName>
            </Td>
            <Td>
              {canEditEmail ? (
                <EditableEmail
                  agent={agent}
                  caseId={caseId}
                  onClose={onClose}
                  {...{ setRefreshDetails }}
                />
              ) : (
                agent.email || "—"
              )}
            </Td>
            <Td>{agent.user?.mobile || agent.mobile || "—"}</Td>
            <Td>
              <RolePill $primary={agent.role === "primary"}>
                {agent.role === "primary" ? "Primary" : "Secondary"}
              </RolePill>
            </Td>
          </TableRow>
        ))}
      </tbody>
    </Table>
  </TableContainer>
);

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(26, 35, 56, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  z-index: 1200;
`;

const ModalWrapper = styled.div`
  background: white;
  border-radius: 22px;
  box-shadow: 0 24px 60px -18px rgba(15, 23, 42, 0.45);
  width: 100%;
  max-width: 860px;
  max-height: 92vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  border: 1px solid rgba(148, 163, 184, 0.35);
`;

const ModalHeader = styled.div`
  padding: 20px 24px;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  background: linear-gradient(180deg, #ffffff 0%, #f8fbff 100%);
`;

const HeaderLeft = styled.div`
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 14px;
`;

const ModalAccent = styled.div`
  width: 6px;
  height: 42px;
  background: ${(props) => props.color};
  border-radius: 9999px;
`;

const HeaderContent = styled.div``;

const ModalTitle = styled.h2`
  font-size: 20px;
  font-weight: 700;
  color: #1e2937;
  margin: 0;
`;

const ModalSubtitle = styled.p`
  font-size: 14px;
  color: #64748b;
  margin: 4px 0 0;
`;

const ModalBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 24px;
  background:
    radial-gradient(
      circle at top left,
      rgba(59, 130, 246, 0.08),
      transparent 30%
    ),
    linear-gradient(180deg, #f8fbff 0%, #f8fafc 100%);
`;

/* Table Styles */
const TableContainer = styled.div`
  border-radius: 18px;
  overflow: hidden;
  border: 1px solid #e2e8f0;
  background: white;
  box-shadow: 0 10px 30px -18px rgba(15, 23, 42, 0.35);
`;

const Table = styled.table`
  width: 100%;
  table-layout: fixed;
  border-collapse: collapse;
`;

const Th = styled.th`
  padding: 12px 18px;
  text-align: left;
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
  background: #f1f5f9;
  border-bottom: 1px solid #e2e8f0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const TableRow = styled.tr`
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #f8fafc;
  }

  td {
    padding: 16px 20px;
    border-bottom: 1px solid #f1f5f9;
  }

  &:last-child td {
    border-bottom: none;
  }
`;

const Td = styled.td`
  color: ${COLORS.COLOR_DARK};
  font-family: ${theme.fonts.primaryFontRegular};
  font-size: 14px;
  vertical-align: middle;
  overflow: visible;
`;

const EmailForm = styled(Form)`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;

  & > div:first-child {
    flex: 1 1 auto;
    min-width: 0;
  }

  label {
    display: none;
  }
  span,
  small,
  p {
    font-size: 11px;
  }
`;

const UpdateButtonWrap = styled.div`
  flex-shrink: 0;

  button {
    width: auto !important;
    min-width: 88px;
    padding: 8px 16px !important;
    font-size: 13px !important;
    height: 38px !important;
    white-space: nowrap;
  }
`;

const AgentName = styled.div`
  font-family: ${theme.fonts.primaryFontSemiBold};
  color: ${COLORS.COLOR_DARK};
`;

const RolePill = styled.span`
  padding: 4px 12px;
  border-radius: 9999px;
  font-size: 13px;
  font-weight: 600;
  background: ${(props) => (props.$primary ? "#ecfdf5" : "#f1f5f9")};
  color: ${(props) => (props.$primary ? "#10b981" : "#64748b")};
`;

/* Empty State */
const EmptyState = styled.div`
  text-align: center;
  padding: 72px 20px;
  color: #64748b;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 18px;
  box-shadow: 0 10px 30px -18px rgba(15, 23, 42, 0.35);
`;

const EmptyTitle = styled.div`
  font-size: 18px;
  font-weight: 600;
  color: #1e2937;
  margin-bottom: 8px;
`;

const EmptySubtitle = styled.div`
  font-size: 14px;
`;

export const Spinner = styled.div`
  width: 28px;
  height: 28px;
  border: 3px solid #e2e8f0;
  border-top-color: #3b82f6;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto;
`;

const CloseWrapper = styled.div`
  margin-left: auto;
  display: flex;
  align-items: center;
  flex-shrink: 0;
  padding-left: 12px;
`;

const HeaderActions = styled.div`
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const AddButton = styled.button`
  background: ${COLORS.BTN_GREEN};
  color: white;
  border: none;
  padding: 9px 16px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s ease;

  &:active {
    transform: translateY(1px);
  }
`;
